/**
 * ─── Entity About Data ──────────────────────────────────────
 * Centralized metadata for each business entity's About page.
 * Each entry describes the features, benefits, and capabilities
 * of a business system module.
 *
 * Architecture: metadata-driven — add a new entry here and the
 * AboutPage component renders it automatically. No per-entity
 * code changes needed.
 */

import type { LucideIcon } from 'lucide-react'

export interface AboutFeature {
  icon: string           // Lucide icon name
  title: string
  description: string
  details: string[]
}

export interface AboutEntityData {
  /** Route path (e.g. '/laundry/about') */
  path: string
  /** Entity system name */
  title: string
  /** Short tagline */
  tagline: string
  /** 1-2 paragraph description */
  description: string
  /** Hero icon name */
  heroIcon: string
  /** Hero accent color (tailwind) */
  heroColor: string
  /** Feature list */
  features: AboutFeature[]
  /** Key benefits */
  benefits: string[]
  /** Stats/metrics (label, value) */
  stats: { label: string; value: string }[]
  /** List of sub-pages/routes this system provides */
  modules: { label: string; path: string; icon: string }[]
}

// ═══════════════════════════════════════════════════════════════
// Cooperative Core
// ═══════════════════════════════════════════════════════════════

const cooperativeAbout: AboutEntityData = {
  path: '/cooperative/about',
  title: 'Cooperative Management',
  tagline: 'Complete member-centric cooperative ERP — from membership to lending to accounting',
  description: `A full-featured Cooperative Management System built for Philippine cooperatives. 
    Manage member records, share capital contributions, savings accounts, loan origination 
    and servicing, payments, collections, and double-entry accounting — all in one integrated 
    platform. Built with metadata-driven customization so each cooperative can adapt the 
    system to their unique bylaws and policies without custom code.`,
  heroIcon: 'Building2',
  heroColor: 'blue',
  features: [
    {
      icon: 'Users',
      title: 'Member Management',
      description: 'Complete member lifecycle — registration, profiling, dependents, membership types, and status tracking.',
      details: [
        'Member registration with Philippine-standard data fields',
        'Membership types: Regular, Associate, Institutional',
        'Status tracking: Active, Inactive, Suspended, Terminated, Deceased',
        'Dependent management with relationship tracking',
        'Member ID generation and card printing support',
        'Bulk import/export for member data migration',
      ],
    },
    {
      icon: 'PiggyBank',
      title: 'Share Capital & Savings',
      description: 'Manage share capital subscriptions and member savings accounts with full transaction history.',
      details: [
        'Share capital subscription tracking with certificate generation',
        'Regular and special savings accounts',
        'Interest computation configurable per cooperative policy',
        'Withdrawal processing with approval workflows',
        'Passbook-style transaction ledgers',
        'Auto-post to double-entry accounting',
      ],
    },
    {
      icon: 'Landmark',
      title: 'Loan Management',
      description: 'End-to-end lending — from application to disbursement to collection to full payment.',
      details: [
        'Loan product configuration with flexible interest types (flat, diminishing, graduated)',
        'Loan application with credit evaluation and approval workflow',
        'Promissory note generation',
        'Payment schedules with amortization tables',
        'Penalty computation for late payments',
        'Loan restructuring and renewal',
        'Aging analysis and delinquency tracking',
      ],
    },
    {
      icon: 'Calculator',
      title: 'Double-Entry Accounting',
      description: 'Full double-entry bookkeeping with chart of accounts, journal entries, and trial balance.',
      details: [
        'Configurable chart of accounts per cooperative',
        'Journal entry creation with automatic debit/credit validation',
        'Trial balance and financial statement generation',
        'Auto-posting from member transactions',
        'Bank reconciliation',
        'Expense tracking with categories',
      ],
    },
    {
      icon: 'Wallet',
      title: 'Collections & Remittance',
      description: 'Field collection management with collector assignments, daily logs, and remittance tracking.',
      details: [
        'Collector management with area assignments',
        'Collection groups for team-based collection',
        'Daily collection sheets with member balances',
        'Remittance processing with over/under tracking',
        'Collection efficiency analytics',
        'GPS-tracked collection routes (mobile)',
      ],
    },
    {
      icon: 'FileText',
      title: 'Governance & Compliance',
      description: 'Board resolutions, committee management, meeting attendance, and statutory fund tracking.',
      details: [
        'Board resolution drafting and approval workflow',
        'Committee membership management',
        'Meeting attendance tracking with quorum checking',
        'Statutory fund allocation (CEDF, SDF, etc.)',
        'Annual report data compilation',
        'Audit trail on all financial transactions',
      ],
    },
  ],
  benefits: [
    'One unified system instead of multiple disconnected tools',
    'Reduces data entry errors with integrated double-entry accounting',
    'Faster loan processing with automated credit evaluation',
    'Better member service with 360° member view',
    'Regulatory compliance with built-in Philippine cooperative standards',
    'Offline-capable — works even without internet, syncs when connected',
  ],
  stats: [
    { label: 'Entity Types', value: '30+' },
    { label: 'Business Modules', value: '6' },
    { label: 'Offline-First', value: '✓' },
    { label: 'Multi-Tenant', value: '✓' },
  ],
  modules: [
    { label: 'Members', path: '/members', icon: 'Users' },
    { label: 'Share Capital', path: '/share-capital', icon: 'PiggyBank' },
    { label: 'Savings', path: '/savings', icon: 'Wallet' },
    { label: 'Loans', path: '/loans', icon: 'Landmark' },
    { label: 'Loan Applications', path: '/loan-applications', icon: 'FileText' },
    { label: 'Payments', path: '/payments', icon: 'DollarSign' },
    { label: 'Accounting', path: '/accounting/chart-of-accounts', icon: 'Calculator' },
    { label: 'Collections', path: '/collectors', icon: 'Truck' },
    { label: 'Governance', path: '/governance', icon: 'Gavel' },
  ],
}

// ═══════════════════════════════════════════════════════════════
// Laundry Shop System
// ═══════════════════════════════════════════════════════════════

const laundryAbout: AboutEntityData = {
  path: '/laundry/about',
  title: 'Laundry Shop Management',
  tagline: 'Multi-branch laundry operations — from order intake to delivery to revenue analytics',
  description: `A comprehensive Laundry Shop Management System designed for single or multi-branch 
    laundry businesses. Manage customer profiles, service catalogs, order processing with 
    status tracking, payments, inventory of supplies, promotional codes, and cash reconciliation. 
    Real-time dashboards give owners visibility across all branches — revenue, pending orders, 
    overdue pickups, and low-stock alerts.`,
  heroIcon: 'Shirt',
  heroColor: 'indigo',
  features: [
    {
      icon: 'Users',
      title: 'Customer Management',
      description: 'Customer profiles with tier-based pricing, loyalty tracking, and order history.',
      details: [
        'Customer registration with contact details and preferences',
        'Tier system: Regular, Silver, Gold, Platinum',
        'Per-customer pricing overrides',
        'Complete order history with quick re-order',
        'Loyalty points accumulation and redemption',
        'Customer communication log (SMS/call notes)',
      ],
    },
    {
      icon: 'Package',
      title: 'Order Processing',
      description: 'End-to-end order lifecycle — intake, washing, drying, folding, pickup/delivery.',
      details: [
        'Order creation with multiple service items per order',
        'Status tracking: Received → Washing → Drying → Folding → Ready → Delivered',
        'Priority levels: Normal, Express, Rush',
        'Pickup & delivery scheduling',
        'Automatic pricing based on service type, weight, and customer tier',
        'Order tags and special instructions',
      ],
    },
    {
      icon: 'Tags',
      title: 'Service Catalog',
      description: 'Configurable service menu with per-kilo and per-piece pricing.',
      details: [
        'Wash, Dry, Fold services with per-kilo pricing',
        'Dry cleaning for delicate garments',
        'Ironing/pressing as standalone or add-on',
        'Comforter, curtain, and specialty item pricing',
        'Service bundles and package deals',
        'Branch-specific pricing overrides',
      ],
    },
    {
      icon: 'DollarSign',
      title: 'Payment Processing',
      description: 'Multiple payment methods, partial payments, and cash reconciliation.',
      details: [
        'Cash, GCash, Maya, bank transfer payment methods',
        'Partial payment tracking with balance due',
        'Payment receipt generation',
        'Daily cash reconciliation per branch',
        'Revenue breakdown by service type and branch',
        'Promo code application with discount computation',
      ],
    },
    {
      icon: 'ClipboardList',
      title: 'Inventory Management',
      description: 'Track detergent, fabric softener, packaging, and other supplies per branch.',
      details: [
        'Inventory item master with reorder points',
        'Stock-in / stock-out tracking per branch',
        'Low stock alerts',
        'Usage tracking by order (consumption)',
        'Supplier management',
        'Inventory valuation',
      ],
    },
    {
      icon: 'BarChart3',
      title: 'Analytics & Reports',
      description: 'Business intelligence dashboards and exportable reports for decision-making.',
      details: [
        'Daily/weekly/monthly revenue reports',
        'Order volume trends by branch',
        'Customer acquisition and retention metrics',
        'Service popularity analysis',
        'Profit margin per order type',
        'Export to Excel/PDF',
      ],
    },
  ],
  benefits: [
    'Manage multiple branches from a single dashboard',
    'Reduce order errors with barcode/QR tracking',
    'Increase repeat customers with loyalty program',
    'Prevent stock-outs with automated reorder alerts',
    'Real-time revenue visibility — know your numbers anytime',
    'Save hours on manual cash reconciliation',
  ],
  stats: [
    { label: 'Entities', value: '7' },
    { label: 'Mock Records', value: '49' },
    { label: 'Status States', value: '7' },
    { label: 'Multi-Branch', value: '✓' },
  ],
  modules: [
    { label: 'Dashboard', path: '/laundry', icon: 'LayoutDashboard' },
    { label: 'Orders', path: '/laundry/orders', icon: 'Package' },
    { label: 'Customers', path: '/laundry/customers', icon: 'Users' },
    { label: 'Services', path: '/laundry/services', icon: 'Tags' },
    { label: 'Payments', path: '/laundry/payments', icon: 'DollarSign' },
    { label: 'Inventory', path: '/laundry/inventory', icon: 'ClipboardList' },
    { label: 'Promos', path: '/laundry/promos', icon: 'Percent' },
    { label: 'Reports', path: '/laundry/reports', icon: 'BarChart3' },
    { label: 'Cash Recon', path: '/laundry/cash-reconciliation', icon: 'Calculator' },
  ],
}

// ═══════════════════════════════════════════════════════════════
// Driving School System
// ═══════════════════════════════════════════════════════════════

const drivingSchoolAbout: AboutEntityData = {
  path: '/driving-school/about',
  title: 'Driving School Management',
  tagline: 'Complete driving school operations — students, instructors, courses, schedules, and vehicles',
  description: `A Driving School Management System that handles the full student lifecycle from 
    enrollment to certification. Manage student records, instructor assignments, course 
    catalogs (theoretical and practical), driving schedules, vehicle fleet, and payment 
    tracking. Perfect for LTO-accredited driving schools with single or multiple branches.`,
  heroIcon: 'Car',
  heroColor: 'red',
  features: [
    {
      icon: 'UserPlus',
      title: 'Student Management',
      description: 'Student profiles with license type tracking, medical clearance, and enrollment history.',
      details: [
        'Student registration with personal and contact information',
        'License type tracking: Student Permit, Non-Pro, Professional',
        'Medical certificate and requirements tracking',
        'Enrollment history across courses',
        'Student progress tracking (theory → practical → certified)',
        'Bulk SMS/email notifications',
      ],
    },
    {
      icon: 'GraduationCap',
      title: 'Course Management',
      description: 'Theoretical and practical driving courses with flexible scheduling.',
      details: [
        'Course catalog: TDC (Theoretical Driving Course), PDC (Practical Driving Course)',
        'Motorcycle and vehicle-specific courses',
        'Course duration and session count configuration',
        'Per-course pricing and discount rules',
        'Course prerequisites enforcement',
        'Certificate generation upon completion',
      ],
    },
    {
      icon: 'Calendar',
      title: 'Schedule Management',
      description: 'Instructor scheduling, vehicle availability, and student session booking.',
      details: [
        'Instructor availability calendar',
        'Vehicle scheduling to prevent double-booking',
        'Student session booking with conflict detection',
        'Rescheduling and cancellation handling',
        'Daily/weekly schedule views',
        'Automated reminder notifications',
      ],
    },
    {
      icon: 'Users',
      title: 'Instructor Management',
      description: 'Instructor profiles, accreditation tracking, and performance monitoring.',
      details: [
        'Instructor profiles with qualifications and certifications',
        'LTO accreditation status tracking',
        'Instructor availability and schedule management',
        'Student assignment and load balancing',
        'Performance metrics (pass rates, student feedback)',
        'Commission/payroll integration',
      ],
    },
    {
      icon: 'Car',
      title: 'Vehicle Fleet Management',
      description: 'Vehicle inventory, maintenance tracking, and insurance management.',
      details: [
        'Vehicle registration with plate number, model, type',
        'Vehicle assignment to instructors',
        'Maintenance schedule and repair history',
        'Insurance and registration renewal reminders',
        'Fuel and expense tracking',
        'Vehicle availability status (active, in-maintenance, retired)',
      ],
    },
    {
      icon: 'DollarSign',
      title: 'Payment Tracking',
      description: 'Enrollment fees, installment plans, and payment history.',
      details: [
        'Enrollment payment processing',
        'Installment plans for courses',
        'Payment history per student',
        'Outstanding balance tracking',
        'Receipt generation',
        'Revenue reports by course, instructor, and branch',
      ],
    },
  ],
  benefits: [
    'Never double-book an instructor or vehicle again',
    'Track every student from enrollment to LTO certification',
    'Automated reminders reduce no-shows',
    'Fleet maintenance tracking prevents breakdowns',
    'Revenue analytics show which courses are most profitable',
    'Multi-branch support for growing driving schools',
  ],
  stats: [
    { label: 'Entity Types', value: '7' },
    { label: 'Course Types', value: '4' },
    { label: 'Schedule Views', value: '2' },
    { label: 'Multi-Branch', value: '✓' },
  ],
  modules: [
    { label: 'Students', path: '/driving-school/students', icon: 'UserPlus' },
    { label: 'Enrollments', path: '/driving-school/enrollments', icon: 'FileText' },
    { label: 'Courses', path: '/driving-school/courses', icon: 'GraduationCap' },
    { label: 'Schedules', path: '/driving-school/schedules', icon: 'Calendar' },
    { label: 'Instructors', path: '/driving-school/instructors', icon: 'Users' },
    { label: 'Vehicles', path: '/driving-school/vehicles', icon: 'Car' },
    { label: 'Payments', path: '/driving-school/payments', icon: 'DollarSign' },
  ],
}

// ═══════════════════════════════════════════════════════════════
// Fast Food (Crispy King)
// ═══════════════════════════════════════════════════════════════

const fastfoodAbout: AboutEntityData = {
  path: '/fastfood/about',
  title: 'Crispy King Fast Food',
  tagline: 'End-to-end fast food operations — menu, orders, inventory, and daily sales tracking',
  description: `A Fast Food Management System purpose-built for quick-service restaurants. 
    Manage your menu catalog with pricing and variants, process dine-in/takeout/delivery 
    orders, track ingredient inventory across branches, and close out each day with 
    automated daily sales summaries. Designed for multi-branch operations with 
    centralized management visibility.`,
  heroIcon: 'UtensilsCrossed',
  heroColor: 'amber',
  features: [
    {
      icon: 'Menu',
      title: 'Menu Management',
      description: 'Product catalog with categories, variants, pricing, and availability control.',
      details: [
        'Menu item master with name, description, and photo',
        'Category organization: Meals, Sides, Drinks, Desserts',
        'Variant support: size (Regular/Large), spice level, add-ons',
        'Per-item and per-variant pricing',
        'Availability toggle (in-stock / sold-out)',
        'Branch-specific menu items and pricing',
      ],
    },
    {
      icon: 'ShoppingBag',
      title: 'Order Processing',
      description: 'POS-style order taking with order types, line items, and status tracking.',
      details: [
        'Order types: Dine-in, Takeout, Delivery',
        'Line-item entry with quantity, variants, and special instructions',
        'Order status: Received → Preparing → Ready → Served/Delivered → Completed',
        'Automatic subtotal, tax, and total computation',
        'Order number generation for pickup tracking',
        'Order history and re-order capabilities',
      ],
    },
    {
      icon: 'ClipboardList',
      title: 'Inventory Management',
      description: 'Ingredient-level inventory tracking with usage deduction and reorder alerts.',
      details: [
        'Ingredient master list with units of measure',
        'Stock-in recording from suppliers',
        'Automatic ingredient deduction per order (recipe-based)',
        'Par levels and reorder point alerts',
        'Waste and spoilage tracking',
        'Inventory valuation and cost analysis',
      ],
    },
    {
      icon: 'BarChart3',
      title: 'Daily Sales Summary',
      description: 'End-of-day sales closing with revenue breakdown, payment summary, and variance.',
      details: [
        'Daily sales auto-computation from orders',
        'Revenue breakdown: food vs. beverages vs. desserts',
        'Payment method summary: Cash, GCash, Card',
        'Cash drawer reconciliation with over/short tracking',
        'Day-over-day and week-over-week comparisons',
        'Per-branch sales aggregation',
      ],
    },
    {
      icon: 'Truck',
      title: 'Supplier & Procurement',
      description: 'Supplier management with purchase orders and delivery tracking.',
      details: [
        'Supplier directory with contact and payment terms',
        'Purchase order creation from low-stock alerts',
        'Delivery receipt recording',
        'Supplier performance tracking',
        'Cost comparison across suppliers',
      ],
    },
    {
      icon: 'Users',
      title: 'Customer & Loyalty',
      description: 'Customer profiles, order history, and loyalty rewards tracking.',
      details: [
        'Customer registration (optional for walk-ins)',
        'Order history per customer',
        'Loyalty points program',
        'Favorite orders for quick re-order',
        'Customer feedback collection',
      ],
    },
  ],
  benefits: [
    'Faster order taking with intuitive POS interface',
    'Never run out of ingredients with automated reorder alerts',
    'Know your daily revenue before closing time',
    'Reduce waste with recipe-based inventory deduction',
    'Multi-branch management from a single dashboard',
    'Data-driven menu decisions based on sales analytics',
  ],
  stats: [
    { label: 'Entity Types', value: '5' },
    { label: 'Order Types', value: '3' },
    { label: 'Status States', value: '5' },
    { label: 'Multi-Branch', value: '✓' },
  ],
  modules: [
    { label: 'Menu', path: '/fastfood/menu', icon: 'Menu' },
    { label: 'Orders', path: '/fastfood/orders', icon: 'ShoppingBag' },
    { label: 'Inventory', path: '/fastfood/inventory', icon: 'ClipboardList' },
    { label: 'Daily Sales', path: '/fastfood/daily-sales', icon: 'BarChart3' },
  ],
}

// ═══════════════════════════════════════════════════════════════
// Clinic Management System
// ═══════════════════════════════════════════════════════════════

const clinicAbout: AboutEntityData = {
  path: '/clinic/about',
  title: 'Clinic Management System',
  tagline: 'Medical clinic operations — patients, doctors, appointments, consultation records, and billing',
  description: `A Clinic Management System for outpatient medical clinics. Manage patient 
    registration and medical history, doctor profiles and schedules, appointment booking 
    with conflict detection, SOAP-format consultation records, and integrated billing 
    with payment tracking. Designed for single or multi-branch clinic operations.`,
  heroIcon: 'HeartPulse',
  heroColor: 'rose',
  features: [
    {
      icon: 'UserPlus',
      title: 'Patient Management',
      description: 'Complete patient profiles with medical history, allergies, and visit records.',
      details: [
        'Patient registration with demographic and contact information',
        'Medical history including chronic conditions and surgeries',
        'Allergy tracking with severity levels',
        'Current medications list',
        'Family medical history',
        'Visit history with one-click access to past consultations',
      ],
    },
    {
      icon: 'Stethoscope',
      title: 'Doctor Management',
      description: 'Doctor profiles with specialization, license tracking, and schedule management.',
      details: [
        'Doctor profiles with PRC license number and specialization',
        'Consultation schedule per doctor',
        'Appointment slot configuration',
        'Doctor availability and leave management',
        'Patient load tracking',
      ],
    },
    {
      icon: 'CalendarCheck',
      title: 'Appointment System',
      description: 'Online-style appointment booking with time slots, status tracking, and reminders.',
      details: [
        'Patient self-booking or staff-assisted booking',
        'Time-slot scheduling per doctor',
        'Appointment status: Pending → Confirmed → Arrived → In-Consultation → Completed → No-Show',
        'Conflict detection to prevent double-booking',
        'Waitlist management for cancelled slots',
        'Automated SMS/email reminders',
      ],
    },
    {
      icon: 'FileText',
      title: 'Consultation Records',
      description: 'SOAP-format medical records with vital signs, diagnosis, and prescriptions.',
      details: [
        'SOAP note structure: Subjective, Objective, Assessment, Plan',
        'Vital signs recording: BP, HR, RR, Temp, O2 Sat',
        'ICD-10 diagnosis coding',
        'Prescription writing with drug, dosage, frequency, duration',
        'Laboratory request generation',
        'Medical certificate issuance',
      ],
    },
    {
      icon: 'DollarSign',
      title: 'Billing & Payments',
      description: 'Consultation billing, pharmacy charges, payment processing, and receipts.',
      details: [
        'Consultation fee billing per visit',
        'Additional charges: procedures, supplies, lab tests',
        'Multiple payment methods: Cash, GCash, Card, HMO',
        'HMO/PhilHealth coverage processing',
        'Official receipt generation',
        'Daily collection report per doctor',
      ],
    },
    {
      icon: 'BarChart3',
      title: 'Clinic Analytics',
      description: 'Patient demographics, visit trends, revenue analytics, and doctor performance.',
      details: [
        'Daily/weekly/monthly patient visit trends',
        'Revenue by doctor, service type, and payment method',
        'Most common diagnoses and prescriptions',
        'Patient demographic analysis',
        'No-show rate tracking',
        'Clinic capacity utilization',
      ],
    },
  ],
  benefits: [
    'Reduce patient wait times with efficient scheduling',
    'Complete medical records in SOAP format — medico-legal compliant',
    'Never lose a patient record — everything is searchable',
    'Faster billing with integrated consultation-to-payment flow',
    'HMO and PhilHealth integration reduces claim processing time',
    'Multi-branch support for growing clinic networks',
  ],
  stats: [
    { label: 'Entity Types', value: '6' },
    { label: 'Appt. States', value: '6' },
    { label: 'SOAP Format', value: '✓' },
    { label: 'ICD-10 Ready', value: '✓' },
  ],
  modules: [
    { label: 'Patients', path: '/clinic/patients', icon: 'UserPlus' },
    { label: 'Appointments', path: '/clinic/appointments', icon: 'CalendarCheck' },
    { label: 'Consultation Records', path: '/clinic/records', icon: 'FileText' },
    { label: 'Billing', path: '/clinic/billing', icon: 'DollarSign' },
    { label: 'Doctors', path: '/clinic/doctors', icon: 'Stethoscope' },
  ],
}

// ═══════════════════════════════════════════════════════════════
// HRIS (Human Resource Information System)
// ═══════════════════════════════════════════════════════════════

const hrisAbout: AboutEntityData = {
  path: '/hris/about',
  title: 'HRIS — Human Resource Information System',
  tagline: 'Philippine-compliant HR & payroll — employees, attendance, biometrics, BIR Form 2316',
  description: `A comprehensive Human Resource Information System built for Philippine 
    businesses. Manage employee master records, time and attendance with biometric 
    device integration (ZKTeco, Suprema, Hikvision), shift scheduling, payroll 
    processing with automatic SSS/PhilHealth/Pag-IBIG contributions, TRAIN Law 
    graduated withholding tax computation, and BIR Form 2316 generation. 
    Fully metadata-driven for per-company customization.`,
  heroIcon: 'Briefcase',
  heroColor: 'teal',
  features: [
    {
      icon: 'UserPlus',
      title: 'Employee Master Records',
      description: 'Complete employee profiles with personal, employment, and government ID data.',
      details: [
        '201-file equivalent: personal info, contact, education, employment history',
        'Employment status: Probationary, Regular, Contractual, Project-Based, Seasonal',
        'Government IDs: SSS, PhilHealth, Pag-IBIG, TIN',
        'Tax status selection: S, S1-S4, ME, ME1-ME4',
        'Pay frequency: Daily, Weekly, Semi-Monthly, Monthly',
        'Employee document attachments (contracts, IDs, certificates)',
      ],
    },
    {
      icon: 'Clock',
      title: 'Time & Attendance',
      description: 'Attendance tracking with multiple input sources and shift management.',
      details: [
        'Check-in / Check-out with break tracking',
        'Attendance sources: Biometric device, Mobile GPS, Manual entry, CSV import',
        'Shift management: Morning, Afternoon, Night, Custom',
        'Overtime tracking with approval workflow',
        'Late and undertime computation',
        'Attendance correction requests',
      ],
    },
    {
      icon: 'Fingerprint',
      title: 'Biometric Integration',
      description: 'Direct device communication with ZKTeco, Suprema, and Hikvision devices.',
      details: [
        'Supported devices: ZKTeco, Suprema, Hikvision, generic fingerprint/face',
        'Protocols: TCP, USB, WiFi, Cloud API',
        'Automatic log pulling on configurable interval',
        'Enrollment ID to Employee ID mapping',
        'Raw log parsing and standardization',
        'Mobile GPS + selfie attendance for field employees',
      ],
    },
    {
      icon: 'Calculator',
      title: 'Philippine Payroll Engine',
      description: 'Full payroll processing with automatic government contributions and tax.',
      details: [
        'Period-based payroll: Semi-monthly, Monthly, Weekly',
        'Automatic SSS contribution table computation',
        'PhilHealth premium calculation (updated 2024 rates)',
        'Pag-IBIG contribution (employee + employer share)',
        'TRAIN Law graduated withholding tax table (2023 onwards)',
        'Overtime pay: Regular OT, Rest Day, Holiday, Night Differential',
        '13th Month Pay computation with pro-rating',
        'De Minimis benefits (non-taxable allowances)',
      ],
    },
    {
      icon: 'Receipt',
      title: 'Payslip & BIR Form 2316',
      description: 'Payslip generation and annual BIR Certificate of Compensation Payment.',
      details: [
        'Per-period payslip with gross pay, deductions, net pay breakdown',
        'Year-to-date accumulation on each payslip',
        'BIR Form 2316 auto-generation per employee',
        'Annualization of withholding tax',
        'Printable payslip and Form 2316 templates',
        'Digital distribution via employee portal',
      ],
    },
    {
      icon: 'FileText',
      title: 'Compliance & Reports',
      description: 'Government-mandated reports and HR analytics dashboard.',
      details: [
        'DOLE-mandated payroll register',
        'SSS R-3 (Contribution Collection List)',
        'PhilHealth ER2 (Employer Remittance Report)',
        'BIR Form 1601-C (Monthly Remittance Return)',
        'Employee headcount and turnover analytics',
        'Department-wise labor cost analysis',
      ],
    },
  ],
  benefits: [
    'Full Philippine compliance — SSS, PhilHealth, Pag-IBIG, BIR-ready',
    'Automatic tax computation using latest TRAIN Law tables',
    'Eliminate manual biometric log processing',
    'Payslips and BIR 2316 generated in seconds, not days',
    'Reduce payroll errors with automated contribution calculations',
    'Mobile attendance for field/remote employees',
  ],
  stats: [
    { label: 'Entity Types', value: '8' },
    { label: 'Tax Statuses', value: '10' },
    { label: 'Device Brands', value: '4' },
    { label: 'BIR Compliant', value: '✓' },
  ],
  modules: [
    { label: 'Employees', path: '/hris/employees', icon: 'UserPlus' },
    { label: 'Payroll', path: '/hris/payroll', icon: 'Calculator' },
    { label: 'Attendance', path: '/hris/attendance', icon: 'Clock' },
    { label: 'Shifts', path: '/hris/shifts', icon: 'Calendar' },
    { label: 'Payslips', path: '/hris/payslips', icon: 'Receipt' },
    { label: 'Form 2316', path: '/hris/form-2316', icon: 'FileText' },
    { label: 'Biometric Devices', path: '/hris/devices', icon: 'Fingerprint' },
  ],
}

// ═══════════════════════════════════════════════════════════════
// Water Station System
// ═══════════════════════════════════════════════════════════════

const waterStationAbout: AboutEntityData = {
  path: '/water-station/about',
  title: 'Water Station Management',
  tagline: 'Water refilling station operations — customers, deliveries, containers, and payments',
  description: `A Water Station Management System for water refilling businesses. 
    Track customer profiles with delivery addresses, manage container inventory 
    (gallons and bottles), schedule and record deliveries, process payments, and 
    monitor container returns. Ideal for single or multi-branch water refilling stations.`,
  heroIcon: 'Droplets',
  heroColor: 'cyan',
  features: [
    {
      icon: 'Users',
      title: 'Customer Management',
      description: 'Customer profiles with delivery addresses, container ownership, and order history.',
      details: [
        'Customer registration with multiple delivery addresses',
        'Container ownership tracking per customer',
        'Regular delivery schedule preferences',
        'Order history and consumption patterns',
        'Customer balance and credit tracking',
        'Route-based customer grouping',
      ],
    },
    {
      icon: 'Truck',
      title: 'Delivery Management',
      description: 'Delivery scheduling, route optimization, and delivery confirmation.',
      details: [
        'Delivery order creation with itemized container types',
        'Delivery schedule: date, time window, and assigned driver',
        'Delivery status: Pending → Out for Delivery → Delivered → Confirmed',
        'Container exchange tracking (empty ↔ full)',
        'Delivery route sheets for drivers',
        'Proof of delivery (signature/photo)',
      ],
    },
    {
      icon: 'Package',
      title: 'Container Management',
      description: 'Container inventory with type tracking, maintenance, and loss prevention.',
      details: [
        'Container types: Slim, Round, 5-Gallon, 1-Liter bottles',
        'Container inventory count per branch',
        'Container status: Available, With Customer, Damaged, Lost',
        'Container loss tracking and customer charging',
        'Container cleaning and maintenance logs',
        'Container purchase and replacement tracking',
      ],
    },
    {
      icon: 'DollarSign',
      title: 'Payment Processing',
      description: 'Payment collection with multiple methods and customer ledger.',
      details: [
        'Per-delivery or per-container pricing',
        'Cash, GCash, and credit payment methods',
        'Customer running balance (utang) tracking',
        'Payment history and collection reports',
        'Daily collection summary per driver/route',
      ],
    },
  ],
  benefits: [
    'Know exactly how many containers are with each customer',
    'Reduce container loss with tracking and customer accountability',
    'Optimize delivery routes to save fuel and time',
    'Never miss a regular customer delivery',
    'Real-time container inventory prevents stock-outs',
  ],
  stats: [
    { label: 'Entity Types', value: '4' },
    { label: 'Container Types', value: '4' },
    { label: 'Delivery Statuses', value: '4' },
    { label: 'Multi-Branch', value: '✓' },
  ],
  modules: [
    { label: 'Customers', path: '/water-station/customers', icon: 'Users' },
    { label: 'Deliveries', path: '/water-station/deliveries', icon: 'Truck' },
  ],
}

// ═══════════════════════════════════════════════════════════════
// Collections System
// ═══════════════════════════════════════════════════════════════

const collectionsAbout: AboutEntityData = {
  path: '/collections/about',
  title: 'Collections & Remittance',
  tagline: 'Field collection management — collectors, areas, daily logs, and remittance tracking',
  description: `A Field Collections Management System for loan collection operations. 
    Assign collectors to geographic areas, create collection groups, track daily 
    collection activities, process remittances with over/under tracking, and 
    monitor collection efficiency. Integrates with the loan management module 
    for real-time member balance updates.`,
  heroIcon: 'Truck',
  heroColor: 'emerald',
  features: [
    {
      icon: 'Users',
      title: 'Collector Management',
      description: 'Collector profiles with area assignments, performance metrics, and commission tracking.',
      details: [
        'Collector registration with personal and employment details',
        'Geographic area assignments per collector',
        'Daily collection targets and achievement tracking',
        'Commission computation based on collections',
        'Collector performance ranking and reports',
      ],
    },
    {
      icon: 'Map',
      title: 'Area Management',
      description: 'Geographic area definition with member assignment and route optimization.',
      details: [
        'Area creation with name, description, and coverage',
        'Member assignment to areas',
        'Collection route planning and sequencing',
        'Area-based collection performance analytics',
      ],
    },
    {
      icon: 'ClipboardList',
      title: 'Daily Collection Logs',
      description: 'Daily collection sheets with member balances, amounts collected, and status.',
      details: [
        'Daily collection sheet generation per collector',
        'Member-wise collection entry with amount and payment type',
        'Collection status: Collected, Partially Paid, Unpaid, Rescheduled',
        'Collection notes and follow-up flagging',
        'Real-time sync with member loan balances',
      ],
    },
    {
      icon: 'DollarSign',
      title: 'Remittance Processing',
      description: 'End-of-day remittance with cash count, variance tracking, and deposit confirmation.',
      details: [
        'Remittance creation from daily collection totals',
        'Over/under tracking with variance explanation',
        'Cash breakdown by denomination',
        'Bank deposit confirmation tracking',
        'Remittance approval workflow',
      ],
    },
  ],
  benefits: [
    'Eliminate paper collection sheets with digital daily logs',
    'Real-time visibility into field collections',
    'Reduce remittance discrepancies with automated variance detection',
    'Improve collector accountability with GPS-verified routes',
    'Faster loan balance updates with integrated collections-to-loans sync',
  ],
  stats: [
    { label: 'Entity Types', value: '4' },
    { label: 'Collection Statuses', value: '4' },
    { label: 'Offline-Capable', value: '✓' },
    { label: 'GPS Tracking', value: '✓' },
  ],
  modules: [
    { label: 'Collectors', path: '/collectors', icon: 'Users' },
    { label: 'Areas', path: '/areas', icon: 'Map' },
    { label: 'Remittances', path: '/remittances', icon: 'DollarSign' },
  ],
}

const arenaAbout: AboutEntityData = {
  path: '/arena/about',
  title: 'Fighting Arena Management',
  tagline: 'Complete arena operations — fighters, matches, membership, betting, and entrance management',
  description: `A comprehensive Fighting Arena Management System for cockfighting arenas and similar venues. 
    Manage fighter profiles with breed, weight class, and win/loss records. Schedule matches with 
    configurable odds and house commission. Track arena membership with tiered privileges (Regular, 
    Silver, Gold, Platinum, VIP). Place and settle bets with automatic payout computation and 
    double-entry ledger integration. Manage entrance tickets and fees.`,
  heroIcon: 'Swords',
  heroColor: 'red',
  features: [
    {
      icon: 'Swords',
      title: 'Fighter Management',
      description: 'Complete fighter profiles with breed, weight class, record tracking, and owner info.',
      details: [
        'Fighter registration with breed, color, age, weight, and weight class',
        'Owner name and contact information',
        'Win/Loss/Draw record tracking updated automatically on match settlement',
        'Status tracking: Active, Injured, Retired, Suspended, Deceased',
        'Weight classes: Lightweight, Welterweight, Middleweight, Heavyweight, Open',
        'Fighter code auto-generation',
      ],
    },
    {
      icon: 'Trophy',
      title: 'Match Scheduling & Settlement',
      description: 'Schedule fights with configurable odds, declare results, auto-settle all bets.',
      details: [
        'Match scheduling with fighter selection and date/time',
        'Configurable decimal odds for each fighter and draw',
        'House commission rate (percentage of winnings)',
        'One-click result declaration: Fighter 1 Win, Fighter 2 Win, Draw',
        'Automatic bet settlement — winning bets paid, losing bets cleared',
        'Fighter records automatically updated on match completion',
      ],
    },
    {
      icon: 'Users',
      title: 'Membership Management',
      description: 'Tiered membership system with Regular, Silver, Gold, Platinum, and VIP levels.',
      details: [
        'Member registration with contact information',
        'Five membership tiers with escalating privileges',
        'Status tracking: Active, Suspended, Expired, Banned',
        'Total bets placed and winnings tracked per member',
        'Entrance history per member',
        'Member code auto-generation',
      ],
    },
    {
      icon: 'Coins',
      title: 'Betting System',
      description: 'Place bets with automatic odds calculation, potential payout display, and settlement.',
      details: [
        'Bet placement: select match, member, fighter (or draw), amount',
        'Automatic potential payout computation (amount × odds)',
        'Bet types: Fighter 1, Fighter 2, Draw',
        'Status tracking: Placed → Won/Lost → Paid',
        'House commission deducted from winning payouts',
        'Complete bet history with settlement audit trail',
      ],
    },
    {
      icon: 'Ticket',
      title: 'Entrance Management',
      description: 'Ticket sales with tiered pricing — General Admission, Ringside, VIP Box, Complimentary.',
      details: [
        'Ticket types: General, Ringside, VIP Box, Complimentary',
        'Per-ticket pricing with quantity support',
        'Payment status tracking: Pending, Paid, Refunded',
        'Member-linked or guest entrances',
        'Revenue tracking per match',
      ],
    },
    {
      icon: 'BarChart3',
      title: 'Arena Analytics',
      description: 'Real-time dashboard with revenue, betting activity, fighter rankings, and match history.',
      details: [
        'Dashboard KPIs: total fighters, matches, members, bets, revenue',
        'Upcoming matches with odds display',
        'Recent bet activity feed',
        'Revenue from betting (house commission) and entrance fees',
        'Fighter rankings by win/loss record',
      ],
    },
  ],
  benefits: [
    'Automatic bet settlement eliminates manual errors',
    'Real-time odds and payout computation',
    'Complete audit trail for all bets and match results',
    'Tiered membership drives repeat attendance',
    'House commission automatically calculated on every winning bet',
    'Offline-capable — syncs data when connectivity returns',
  ],
  stats: [
    { label: 'Entity Types', value: '6' },
    { label: 'Weight Classes', value: '5' },
    { label: 'Member Tiers', value: '5' },
    { label: 'Audit Trail', value: '✓' },
  ],
  modules: [
    { label: 'Dashboard', path: '/arena', icon: 'LayoutDashboard' },
    { label: 'Fighters', path: '/arena/fighters', icon: 'Swords' },
    { label: 'Matches', path: '/arena/matches', icon: 'Trophy' },
    { label: 'Members', path: '/arena/members', icon: 'Users' },
    { label: 'Bets', path: '/arena/bets', icon: 'Coins' },
  ],
}

// ═══════════════════════════════════════════════════════════════
// Pawnshop System
// ═══════════════════════════════════════════════════════════════

const pawnshopAbout: AboutEntityData = {
  path: '/pawnshop/about',
  title: 'Pawnshop Management System',
  tagline: 'Complete pawnshop operations — customers, items, tickets, payments, auctions with double-entry ledger',
  description: `A comprehensive Pawnshop Management System with DDD aggregate design (PawnTicket as aggregate root)
    and metadata-driven configuration. Manage customer profiles with valid ID tracking, catalog pawned items 
    with appraisal values and categories (jewelry, gadgets, appliances, vehicles, firearms, antiques), 
    create pawn tickets with automatic interest and net proceeds computation, track payments (interest, 
    principal, renewal, redemption), and manage expired items through auction with surplus return to pawners.
    Every financial transaction produces double-entry ledger entries for complete audit compliance.`,
  heroIcon: 'Gem',
  heroColor: 'green',
  features: [
    {
      icon: 'Users',
      title: 'Customer Management',
      description: 'Pawner profiles with valid ID verification, contact details, and pawn history.',
      details: [
        'Customer registration with valid ID type and number (Driver\'s License, Passport, UMID)',
        'Contact information and address tracking',
        'Total pawned, redeemed, and auctioned counts per customer',
        'Status tracking: Active, Blocked, Blacklisted',
        'Individual and business customer types',
        'Customer code auto-generation',
      ],
    },
    {
      icon: 'Gem',
      title: 'Item Appraisal & Catalog',
      description: 'Catalog all pawned items with appraisal, categories, condition, and photos.',
      details: [
        'Item categories: Jewelry, Gadget, Appliance, Vehicle, Firearm, Antique, Other',
        'Condition grading: Excellent, Good, Fair, Poor',
        'Appraisal value and auto-computed pawn value (80% of appraisal)',
        'Gold jewelry: weight (grams) and karat tracking',
        'Brand, model, and serial number for electronics',
        'Status tracking per item: Pawned, Redeemed, Auctioned, Expired',
      ],
    },
    {
      icon: 'FileText',
      title: 'Pawn Ticket (Aggregate Root)',
      description: 'Create pawn tickets with automatic interest computation, net proceeds, and maturity dates.',
      details: [
        'Automatic ticket number generation (PT-YYYYMMDD-XXXX)',
        'Interest rate configurable per ticket (default 3% monthly)',
        'Advance interest deduction from principal',
        'Service charge computation (1% of principal)',
        'Net proceeds = Principal − Advance Interest − Service Charge',
        'Maturity date: 30 days from pawn date',
        'Grace period before auction: 90 days (configurable)',
        'Multiple items per ticket',
      ],
    },
    {
      icon: 'DollarSign',
      title: 'Payment Processing',
      description: 'Track all payments with type, method, and remaining balance computation.',
      details: [
        'Payment types: Interest, Principal, Renewal, Redemption, Partial',
        'Payment methods: Cash, GCash, Bank Transfer, Maya',
        'Reference number tracking for digital payments',
        'Auto-computed remaining balance after each payment',
        'Receipt number generation',
      ],
    },
    {
      icon: 'Gavel',
      title: 'Auction Management',
      description: 'Process expired items for auction with minimum bid and surplus return to pawner.',
      details: [
        'Automatic auction eligibility on ticket expiry',
        'Minimum bid based on outstanding loan balance',
        'Sold amount and buyer information recording',
        'Surplus computation: Sold Amount − Outstanding Balance',
        'Configurable surplus policy: Return to pawner or Keep',
        'Auction status: Pending, Listed, Sold, Unsold, Returned',
      ],
    },
    {
      icon: 'Calculator',
      title: 'Double-Entry Ledger',
      description: 'Every transaction produces balanced ledger entries for complete audit compliance.',
      details: [
        'Pawn: DR Customer Loan Receivable / CR Cash + Interest Income + Service Charge',
        'Redemption: DR Cash / CR Customer Loan Receivable + Interest Income',
        'Renewal: DR Cash / CR Interest Income',
        'Auction: DR Cash / CR Loan Receivable + Customer Surplus Payable',
        'Real-time ledger display on every transaction',
        'Metadata-driven interest rates and policies per tenant',
      ],
    },
  ],
  benefits: [
    'Automatic interest and net proceeds computation eliminates calculation errors',
    'Double-entry ledger ensures regulatory compliance and audit readiness',
    'Real-time dashboard shows active tickets, overdue items, and redemption rates',
    'Metadata-driven: interest rates, grace periods, surplus policies — all configurable per branch',
    'Complete item tracking from pawn to redemption or auction',
    'Offline-capable — works without internet, syncs when connected',
  ],
  stats: [
    { label: 'Entity Types', value: '5' },
    { label: 'Item Categories', value: '7' },
    { label: 'DDD Aggregate', value: 'PawnTicket' },
    { label: 'Double-Entry', value: '✓' },
  ],
  modules: [
    { label: 'Dashboard', path: '/pawnshop', icon: 'LayoutDashboard' },
    { label: 'Customers', path: '/pawnshop/customers', icon: 'Users' },
    { label: 'Items', path: '/pawnshop/items', icon: 'Gem' },
    { label: 'Tickets', path: '/pawnshop/tickets', icon: 'FileText' },
  ],
}

// ═══════════════════════════════════════════════════════════════
// Jewelry Shop System
// ═══════════════════════════════════════════════════════════════

const jewelryAbout: AboutEntityData = {
  path: '/jewelry/about',
  title: 'Jewelry Shop Management',
  tagline: 'Complete jewelry retail — inventory priced by metal rate, POS with old-gold trade-ins, repairs, consignment, KYC/AMLA compliance',
  description: 'A comprehensive Jewelry Shop Management System for Philippine jewelry retailers. Manage inventory priced by live metal weight and rate, run point-of-sale with old-gold exchange (palit-luma) and installment plans, track repair and custom-order jobs through a strict state machine, handle consignment stock with commission settlement, and stay compliant with AMLA covered-transaction reporting.',
  heroIcon: 'Gem',
  heroColor: 'purple',
  features: [
    { icon: 'Gem', title: 'Inventory & Catalog', description: 'Item lifecycle management with tag numbers, metal properties, stone specs, and photos.', details: ['Tag/SKU generation and tracking', 'Metal type: Gold, Silver, Platinum, Palladium, Mixed', 'Purity: Karat (10K-24K) or Fineness', 'Stone specifications: type, carat, clarity, certification', 'Status pipeline: In Stock → Reserved → Sold → In Repair → Consigned → Transferred', 'Showcase and vault location tracking', 'Immutable weight and purity after intake (audit-logged corrections only)'] },
    { icon: 'TrendingUp', title: 'Dynamic Pricing Engine', description: 'PriceCalculator pure function — metal weight × rate × purity + making charge + wastage.', details: ['Daily rate cards per metal type and purity', 'Auto-calculate price: weight × rate × purityFactor + making charge + wastage', 'Making charge: Fixed, Per Gram, or % of Metal Value', 'Wastage (melting loss) as configurable percentage', 'Old-gold exchange credit computation (palit-luma)', 'Discount: percentage or fixed amount', 'Price snapshot on order line — never recomputed from later rates'] },
    { icon: 'ShoppingBag', title: 'POS & Checkout', description: 'Fast checkout with PriceCalculator integration, split payments, and receipt generation.', details: ['Click-to-add cart from available inventory', 'Auto price computation via current RateCard', 'Payment methods: Cash, Card, GCash, Maya, Bank Transfer, Installment', 'Amount tendered with change computation', 'Old-gold exchange as trade-in credit', 'Post-sale automatic item status update'] },
    { icon: 'Wrench', title: 'Repairs & Custom Orders', description: 'Strict state machine: Received → Appraised → In Progress → Ready → Delivered.', details: ['Job types: Repair and Custom Order', 'Enforced state machine — no free-text status', 'Cost estimation with actual vs. estimated tracking', 'Goldsmith assignment', 'Before/after photo documentation', 'Customer notification at status changes'] },
    { icon: 'Handshake', title: 'Consignment Management', description: 'Third-party stock tracking with commission settlement.', details: ['Consignor onboarding with contact and address', 'Commission percentage configuration', 'Total items value and sold value tracking', 'Automatic commission computation', 'Settlement reporting'] },
    { icon: 'Users', title: 'Customer & Compliance', description: 'Customer profiles, loyalty tiers, KYC/AMLA covered transaction reporting.', details: ['Customer registration with contact details', 'Loyalty tiers: Regular → Silver → Gold → Platinum (based on total purchases)', 'KYC document capture (valid ID + selfie)', 'AMLA covered transaction trigger at configurable threshold (default PHP 1,000,000)', 'Purchase history and order tracking'] },
  ],
  benefits: ['Price transparency with full breakdown on every receipt', 'Inventory protection — weight and purity immutable after intake', 'AMLA compliance built-in, not an afterthought', 'Old-gold exchange handled as first-class workflow, not a workaround', 'Repair state machine prevents lost or forgotten jobs', 'Offline-capable — sell and receive stock without internet'],
  stats: [{ label: 'Entity Types', value: '6' }, { label: 'Bounded Contexts', value: '7' }, { label: 'Item Statuses', value: '7' }, { label: 'AMLA Ready', value: 'Yes' }],
  modules: [{ label: 'Dashboard', path: '/jewelry', icon: 'LayoutDashboard' }, { label: 'Inventory', path: '/jewelry/items', icon: 'Gem' }, { label: 'POS', path: '/jewelry/pos', icon: 'ShoppingBag' }],
}

// ═══════════════════════════════════════════════════════════════
// Export
// ═══════════════════════════════════════════════════════════════

export const ALL_ENTITY_ABOUT_DATA: AboutEntityData[] = [
  cooperativeAbout,
  laundryAbout,
  drivingSchoolAbout,
  fastfoodAbout,
  clinicAbout,
  hrisAbout,
  waterStationAbout,
  collectionsAbout,
  arenaAbout,
  pawnshopAbout,
  jewelryAbout,
]

export function getAboutData(path: string): AboutEntityData | undefined {
  return ALL_ENTITY_ABOUT_DATA.find((d) => d.path === path)
}
